import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuestionnaire } from 'hooks/useQuestionnaire';
import { useTabContext } from 'context/TabContext';
import { Box, Button } from '@chakra-ui/react';
import Heading from 'components/Heading/Heading';
import Checkbox from 'components/Checkbox/Checkbox';
import SectionComponent from 'components/SectionComponent/SectionComponent';
import { useLoading } from 'hooks/useLoading';
import { useSession } from 'context/SessionContext';
import { Section } from 'types';
import { useUserSession } from 'context/UserContext';
import { initializeDefaultValues, resetFormValues } from 'utils/formUtils';
import { fetchQuestions as handleFetchQuestions } from 'hooks/useFetchQuestions';

const Residence: React.FC = () => {
  const currentTab = 3;
  const { completeTab, activeTab, loading, setLoading, resolveEmptyTab, enabledTabs, setEmptyTabs } = useTabContext();
  const { startLoading, stopLoading, LoadingSpinner } = useLoading();
  const [residenceData, setResidenceData] = useState<Section[]>([]);
  const { saveQuestionnaire, data } = useSession();
  const { validateStep, validateInputs, validateSubQuestion, editValues } =
    useQuestionnaire();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    setValue,
  } = useForm();
  
  const { userData, getDynamicQuestionnaire } = useUserSession();
  const questionnaireCode = useMemo(() => getDynamicQuestionnaire('RES')?.value ?? '', [userData])
  const baseQuestionnaireCode = useMemo(() => getDynamicQuestionnaire('BAS')?.value ?? '', [userData])

  useEffect(() => {
    if (activeTab === currentTab) {
      if(!data.answerQuestionnaires.find((q: any) => q.questionnaireId === baseQuestionnaireCode).showResidenceTab) {
          resolveEmptyTab(activeTab);
          return;
      }

      setEmptyTabs(prev => prev.filter(tab => tab !== activeTab));
      const fetchResidenceQuestions = async () => {
        startLoading();
        try {
          
          const { sections } = await handleFetchQuestions(questionnaireCode, 'A');

           setResidenceData(sections);
        } catch (error: any) {
          console.error('Erro no request.', error);
        } finally {
          stopLoading();
        }
      };

      if(residenceData.length === 0) fetchResidenceQuestions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!enabledTabs.includes(currentTab)) {
      resetFormValues(residenceData, setValue, reset, initializeDefaultValues(residenceData));
    }
  }, [enabledTabs]);

  const handleSubmitResidence = () => {
    setLoading(true);

    const residenceValues = getValues();
    validateStep(residenceValues);
    const answersResidence = editValues(getValues);

    const answerResidenceData = {
      questionnaireId: questionnaireCode,
      activityCode: 'A',
      answers: [...answersResidence],
    };

    saveQuestionnaire(answerResidenceData);

    try {
      completeTab(activeTab);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box>
        <Heading title="Residência" />
      </Box>
      <Box>
        <form onSubmit={handleSubmit(handleSubmitResidence)}>
          {residenceData.map(sectionList => (
            <SectionComponent
              key={sectionList.code}
              sectionList={sectionList}
              control={control}
              validateInputs={validateInputs}
              getValues={getValues}
              validateSubQuestion={validateSubQuestion}
              errors={errors}
              handleDataChange={setResidenceData}
              dataListChange={residenceData}
            />
          ))}

          <Checkbox
            name="confirmInfo4"
            control={control}
            label="Confirmo a Informação"
            rules={{ required: 'Campo de preenchimento obrigatório' }}
            error={errors?.['confirmInfo4']}
          />

          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button
              variant={'primary'}
              type="submit"
              isLoading={loading}
              loadingText="A Enviar"
            >
              <span>Continuar</span>
            </Button>
          </Box>
        </form>
      </Box>
      <LoadingSpinner />
    </Box>
  );
};

export default Residence;
